/**
 * Client ↔ Google Calendar event matching, mirroring the ClientOverview Events tab
 * (fetchClientEvents). The Events tab is the source of truth for "which meetings
 * involved this client" — canceled events disappear from the calendar and
 * reschedules move, so the calendar is ground truth. Meeting Mode reuses this so
 * its "Met with" / "Upcoming booked" lists agree with the Events tab.
 *
 * Keep the matching rules here in sync with ClientOverview.fetchClientEvents.
 */

import { accountAliasEmails } from './contactEmailAliases';

/** For matching Google invites: attendee list may omit organizer; Gmail +tags / dots should still match roster. */
export function canonicalCalendarEmailKey(email) {
  const raw = String(email || '').trim().toLowerCase();
  if (!raw) return '';
  const at = raw.lastIndexOf('@');
  if (at < 1) return raw;
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  if (domain === 'gmail.com' || domain === 'googlemail.com') {
    const baseLocal = local.split('+')[0]?.replace(/\./g, '') || '';
    return `${baseLocal}@${domain}`;
  }
  return raw;
}

/** Normalize invitee display strings for comparisons when Calendar omits attendee emails. */
export function calendarParticipantDisplayNormalized(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

export function calendarNamesLikelyMatch(rosterName, calendarName) {
  const a = calendarParticipantDisplayNormalized(rosterName);
  const b = calendarParticipantDisplayNormalized(calendarName);
  if (!a || !b) return false;
  if (a === b) return true;
  const aParts = a.split(' ').filter(Boolean);
  const bParts = b.split(' ').filter(Boolean);
  if (aParts.length >= 2 && bParts.length >= 2) {
    return aParts[0] === bParts[0] && aParts[aParts.length - 1] === bParts[bParts.length - 1];
  }
  return a.includes(b) || b.includes(a);
}

/**
 * Build the client-side matching roster for one account: canonical email keys,
 * raw lowercase emails (for description/title text hints), the primary contact's
 * normalized display name, and the roster members (for display-name matching).
 * `members` should come from fetchClientEventRosterMembers(account).
 */
export function buildClientEventRoster(account, members) {
  const rosterKeys = new Set();
  const rosterRawLowerEmails = new Set();

  const addRosterEmail = (email) => {
    const t = String(email || '').trim();
    if (!t) return;
    rosterRawLowerEmails.add(t.toLowerCase());
    const key = canonicalCalendarEmailKey(t);
    if (key) rosterKeys.add(key);
  };

  addRosterEmail(account?.contact?.email);
  addRosterEmail(account?.email);
  addRosterEmail(account?.discovery?.email);
  accountAliasEmails(account).forEach(addRosterEmail);
  for (const m of members || []) {
    addRosterEmail(m.email);
    for (const alt of m.alternateEmails || []) addRosterEmail(alt);
  }

  const primaryContactDn = calendarParticipantDisplayNormalized(
    [account?.contact?.firstName, account?.contact?.lastName].filter(Boolean).join(' ')
  );

  return { rosterKeys, rosterRawLowerEmails, primaryContactDn, members: members || [] };
}

/**
 * Same decision the Events tab makes: does this calendar event involve this client?
 * Checks attendee emails (canonicalized), attendee display names against the primary
 * contact and roster member names, organizer/creator emails, then falls back to
 * looking for roster emails inside the event's title/description/location text.
 */
export function eventMatchesClientRoster(event, roster) {
  const { rosterKeys, rosterRawLowerEmails, primaryContactDn, members } = roster;

  const touchesEmailField = (em) => {
    const k = canonicalCalendarEmailKey(em);
    return !!(k && rosterKeys.has(k));
  };

  const attendeeMatches = (a) => {
    if (touchesEmailField(a?.email)) return true;
    const dn = calendarParticipantDisplayNormalized(a?.displayName);
    if (!dn) return false;
    if (primaryContactDn && dn === primaryContactDn) return true;
    for (const m of members) {
      if (calendarNamesLikelyMatch(m.name, dn)) return true;
    }
    return false;
  };

  for (const a of event.attendees || []) {
    if (attendeeMatches(a)) return true;
  }
  if (touchesEmailField(event.organizer?.email)) return true;
  if (touchesEmailField(event.creator?.email)) return true;

  const blob = [event.title, event.summary, event.description, event.location]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();
  for (const em of rosterRawLowerEmails) {
    if (em.length >= 5 && blob.includes(em)) return true;
  }

  return false;
}

/** Stable dedupe key for one calendar event seen from multiple internal calendars. */
export function calendarEventDedupeKey(event) {
  const rawGoogleId = event.googleEventId || null;
  const prefixedId = event.id || null;
  return (
    rawGoogleId ||
    (prefixedId ? prefixedId.replace(/^google_/, '') : null) ||
    `nokey_${event.title || event.summary || ''}_${event.start || event.startTime || ''}`
  );
}

/**
 * Resolve the internal host of an event, same as the Events tab: organizer/creator
 * email (or the calendar-id hint when the organizer is "primary") matched against
 * internal users, falling back to the user whose calendar the event came from.
 */
export function resolveCalendarEventHost(event, fetchedByUser, internalUsers) {
  const rawOrgEmail = event.organizer?.email || event.creator?.email;
  const gcs = event.googleCalendarId;
  const calendarEmailHint = typeof gcs === 'string' && gcs.includes('@') ? gcs : '';
  const organizerForLookup =
    rawOrgEmail && String(rawOrgEmail).trim().toLowerCase() !== 'primary'
      ? rawOrgEmail
      : calendarEmailHint;

  let host = organizerForLookup
    ? (internalUsers || []).find(
        (u) => canonicalCalendarEmailKey(u.email) === canonicalCalendarEmailKey(organizerForLookup)
      )
    : null;
  if (!host) host = fetchedByUser || null;

  if (host) {
    return {
      id: host.id,
      name: host.name,
      profileImage: host.profileImage || null,
      initials: host.initials || '',
      email: host.email,
    };
  }
  const fallbackName =
    event.organizer?.displayName ||
    event.creator?.displayName ||
    event.organizer?.email ||
    event.creator?.email ||
    'Unknown';
  return {
    id: null,
    name: fallbackName,
    profileImage: null,
    initials: String(fallbackName).substring(0, 2).toUpperCase(),
    email: event.organizer?.email || event.creator?.email || '',
  };
}
